import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { CitaContext } from '../../context/CitaContext';
import { useNavigate } from 'react-router-dom';
import { crearCitaPublica, obtenerDisponibilidadPublica } from '../../api/citaService';

const LABEL_METODO_PAGO = {
  tarjeta: 'Tarjeta de credito/debito',
  efectivo: 'Efectivo',
  transferencia: 'Transferencia bancaria'
};

const DetalleItem = ({ label, value, highlight }) => (
  <>
    <div className="confirmation-details-key">{label}</div>
    <div className={highlight ? 'confirmation-details-total' : undefined}>{value}</div>
  </>
);

const ERROR_CONFLICTO_HORARIO = 'slot-conflict';

const crearErrorConflictoHorario = () => ({
  type: ERROR_CONFLICTO_HORARIO,
  message: 'El horario seleccionado ya fue reservado por otro paciente. Por favor, elija un nuevo horario.'
});

const esConflictoHorario = (err) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  const message = (err?.response?.data?.message || '').toLowerCase();

  if (code === 'SLOT_UNAVAILABLE') return true;

  if (status === 400 || status === 409) {
    return message.includes('ya existe una cita') || message.includes('fecha y hora') || message.includes('ocupad');
  }

  return false;
};

const Confirmacion = () => {
  const {
    servicioSeleccionado,
    datosCliente,
    fechaSeleccionada,
    horaSeleccionada,
    metodoPago,
    comprobantePago,
    resetearFormulario,
    setHoraSeleccionada,
    cargarDisponibilidadFecha
  } = useContext(CitaContext);

  const navigate = useNavigate();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [citaGuardada, setCitaGuardada] = useState(false);
  const [datosConfirmacion, setDatosConfirmacion] = useState(null);
  const [previewComprobanteUrl, setPreviewComprobanteUrl] = useState(null);

  const yaIntentadoGuardar = useRef(false);

  const guardarCita = useCallback(async () => {
    if (citaGuardada || !servicioSeleccionado || !fechaSeleccionada || !horaSeleccionada) return;

    try {
      setGuardando(true);
      setError(null);

      const fechaFormateada = fechaSeleccionada.toISOString().split('T')[0];

      const disponibilidadActual = await obtenerDisponibilidadPublica(
        servicioSeleccionado.doctorId,
        fechaFormateada
      );

      const horarioDisponible = disponibilidadActual?.data?.data?.horarios?.some(
        (slot) => slot.hora === horaSeleccionada && slot.disponible
      );

      if (!horarioDisponible) {
        await cargarDisponibilidadFecha(servicioSeleccionado.doctorId, fechaSeleccionada);
        setError(crearErrorConflictoHorario());
        return;
      }

      const citaData = {
        doctorId: servicioSeleccionado.doctorId,
        servicioId: servicioSeleccionado.id,
        fecha: fechaFormateada,
        horaInicio: horaSeleccionada,
        motivoConsulta: '',
        paciente: {
          nombre: datosCliente.nombre,
          apellido: datosCliente.apellido,
          cedula: datosCliente.cedula,
          email: datosCliente.email,
          telefono: datosCliente.telefono
        }
      };

      if (metodoPago) citaData.metodoPago = metodoPago.toUpperCase();

      const respuesta = await crearCitaPublica(citaData);
      setDatosConfirmacion(respuesta.data.data);

      await cargarDisponibilidadFecha(servicioSeleccionado.doctorId, fechaSeleccionada);

      setCitaGuardada(true);
    } catch (err) {
      let mensajeError = 'Ha ocurrido un error al guardar la cita. Por favor, intente nuevamente.';

      if (esConflictoHorario(err)) {
        await cargarDisponibilidadFecha(servicioSeleccionado.doctorId, fechaSeleccionada);
        setError(crearErrorConflictoHorario());
        return;
      }

      if (err.response?.data?.message) {
        mensajeError = err.response.data.message;
      }

      setError({ type: 'generic', message: mensajeError });
    } finally {
      setGuardando(false);
    }
  }, [
    citaGuardada,
    servicioSeleccionado,
    fechaSeleccionada,
    horaSeleccionada,
    datosCliente,
    metodoPago,
    cargarDisponibilidadFecha
  ]);

  useEffect(() => {
    if (!citaGuardada && !guardando && !yaIntentadoGuardar.current) {
      yaIntentadoGuardar.current = true;
      guardarCita();
    }
  }, [citaGuardada, guardando, guardarCita]);

  useEffect(() => {
    if (!comprobantePago) {
      setPreviewComprobanteUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(comprobantePago);
    setPreviewComprobanteUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [comprobantePago]);


  const handleVolverInicio = () => {
    resetearFormulario();
    navigate('/');
  };

  const handleElegirOtroHorario = async () => {
    if (servicioSeleccionado?.doctorId && fechaSeleccionada) {
      await cargarDisponibilidadFecha(servicioSeleccionado.doctorId, fechaSeleccionada);
    }

    setHoraSeleccionada(null);

    navigate('/agendar-cita', {
      state: {
        pasoInicial: 2,
        errorAgenda: 'El horario seleccionado ya no esta disponible. Elija otro horario para completar su cita.'
      }
    });
  };

  const formatoFecha = (fecha) => {
    return fecha.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (error) {
    return (
      <div className="confirmation-wrapper">
        <div className="confirmation-hero error">
          <div className="confirmation-icon-circle error">
            <i className="fas fa-exclamation-circle" style={{ fontSize: '40px', color: '#e53e3e' }}></i>
          </div>
          <h2 className="confirmation-title" style={{ color: '#e53e3e' }}>
            Error al procesar la cita
          </h2>
          <p className="confirmation-description">{error.message}</p>

          <div className="confirmation-actions-row">
            {error.type === ERROR_CONFLICTO_HORARIO ? (
              <button
                onClick={handleElegirOtroHorario}
                className="booking-btn booking-btn-primary"
                type="button"
              >
                <i className="fas fa-calendar-alt"></i>
                Elegir otro horario
              </button>
            ) : (
              <button
                onClick={guardarCita}
                disabled={guardando}
                className="booking-btn booking-btn-primary"
                style={{ opacity: guardando ? 0.7 : 1 }}
                type="button"
              >
                <i className="fas fa-sync-alt"></i>
                {guardando ? 'Procesando...' : 'Intentar nuevamente'}
              </button>
            )}

            <button
              onClick={handleVolverInicio}
              className="booking-btn booking-btn-secondary"
              type="button"
            >
              <i className="fas fa-home"></i>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (guardando) {
    return (
      <div className="confirmation-wrapper" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div className="booking-spinner" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
          <div className="booking-spinner-ring"></div>
          <div className="booking-spinner-icon">
            <i className="fas fa-calendar-check"></i>
          </div>
        </div>

        <h2 className="confirmation-title">
          Procesando su cita
        </h2>

        <p style={{ fontSize: '16px', color: '#666', maxWidth: '450px', margin: '0 auto' }}>
          Estamos guardando los datos de su cita. Por favor, espere un momento...
        </p>
      </div>
    );
  }

  const detallesCita = [
    { label: 'Servicio:', value: servicioSeleccionado?.titulo || servicioSeleccionado?.nombre || '-' },
    { label: 'Fecha:', value: fechaSeleccionada ? formatoFecha(fechaSeleccionada) : '-' },
    { label: 'Hora:', value: horaSeleccionada || '-' },
    { label: 'Paciente:', value: `${datosCliente.nombre} ${datosCliente.apellido}`.trim() },
    { label: 'Metodo de pago:', value: LABEL_METODO_PAGO[metodoPago] || 'No definido' },
    { label: 'Total:', value: `$${servicioSeleccionado?.precio || 0}`, highlight: true }
  ];

  const numeroConfirmacion =
    datosConfirmacion?.numeroConfirmacion
    || datosConfirmacion?.numero
    || 'Pendiente';

  return (
    <div className="confirmation-wrapper">
      <div className="confirmation-hero success">
        <div className="confirmation-icon-circle success">
          <i className="fas fa-check-circle" style={{ fontSize: '40px', color: '#6a5acd' }}></i>
        </div>

        <h2 className="confirmation-title">Cita confirmada</h2>

        <p className="confirmation-description">
          Su cita ha sido agendada exitosamente
        </p>

        {datosConfirmacion && (
          <div className="confirmation-chip">
            Numero de confirmacion: {numeroConfirmacion}
          </div>
        )}
      </div>

      <div className="confirmation-layout">
        <div>
          <div className="confirmation-highlights">
            <div className="confirmation-highlight-card">
              <div className="confirmation-highlight-label">Fecha</div>
              <div className="confirmation-highlight-value">{fechaSeleccionada ? formatoFecha(fechaSeleccionada) : '-'}</div>
            </div>
            <div className="confirmation-highlight-card">
              <div className="confirmation-highlight-label">Hora</div>
              <div className="confirmation-highlight-value">{horaSeleccionada || '-'}</div>
            </div>
            <div className="confirmation-highlight-card">
              <div className="confirmation-highlight-label">Metodo de pago</div>
              <div className="confirmation-highlight-value">{LABEL_METODO_PAGO[metodoPago] || 'No definido'}</div>
            </div>
            <div className="confirmation-highlight-card">
              <div className="confirmation-highlight-label">Total</div>
              <div className="confirmation-highlight-value">${servicioSeleccionado?.precio || 0}</div>
            </div>
          </div>

          <div className="confirmation-details-card">
            <h3 className="confirmation-details-title">
              <i className="fas fa-info-circle" style={{ color: '#6a5acd' }}></i>
              Detalles de la cita
            </h3>

            <div className="confirmation-details-grid">
              {detallesCita.map((item) => (
                <DetalleItem key={item.label} label={item.label} value={item.value} highlight={item.highlight} />
              ))}
            </div>

            {metodoPago === 'transferencia' && comprobantePago && (
              <div className="confirmation-transfer-box">
                <h4 className="confirmation-transfer-title">
                  <i className="fas fa-receipt" style={{ color: '#6a5acd' }}></i>
                  Comprobante de pago cargado
                </h4>

                <div className="confirmation-transfer-preview">
                  <img
                    src={previewComprobanteUrl || undefined}
                    alt="Comprobante de pago"
                  />
                  <div className="confirmation-transfer-chip">
                    <i className="fas fa-check-circle"></i>
                    Comprobante recibido correctamente
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="confirmation-side-card">
          <h4 className="confirmation-side-title">
            <i className="fas fa-route" style={{ color: '#6a5acd' }}></i>
            Proximos pasos
          </h4>
          <ol className="confirmation-side-list">
            <li>Revise que la fecha y hora sean correctas.</li>
            <li>Guarde su numero de confirmacion.</li>
            <li>Llegue 15 minutos antes de su cita.</li>
            <li>Si necesita cambios, contacte a la clinica con anticipacion.</li>
          </ol>

          <h4 className="confirmation-side-title" style={{ marginTop: '16px' }}>
            <i className="fas fa-shield-alt" style={{ color: '#6a5acd' }}></i>
            Estado
          </h4>
          <p style={{ margin: 0, color: '#5a6280', fontSize: '14px' }}>
            La cita queda registrada en estado pendiente de atencion.
          </p>
        </aside>
      </div>

      <div className="confirmation-note">
        <i className="fas fa-envelope" style={{ color: '#6a5acd', fontSize: '20px' }}></i>
        <p>
          Ya esta,<strong> {datosCliente.nombre}</strong>, recuerde llegar 15 minutos antes.
        </p>
      </div>

      <div className="confirmation-actions">
        <button
          onClick={handleVolverInicio}
          className="booking-btn booking-btn-primary"
        >
          <i className="fas fa-home"></i>
          Volver al inicio
        </button>
      </div>
    </div>
  );
};

export default Confirmacion;
